import './WatchedPagesSection.scss';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DataTable as PrimeDataTable } from 'primereact/datatable';
import { DataTable } from '../../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { Skeleton } from 'primereact/skeleton';
import { format } from 'date-fns';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import CustomLink from '../../../../shared/components/custom-link/CustomLink';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import { MetadataBadge } from '../../../../shared/components/metadata-badge/MetadataBadge';
import { getVerifiedBrandStatus, isFirmMentionedInBrands } from '../../helpers/SourcesHelper';
import { TagsResponse } from '../../../../shared/models/tags/TagsResponse';
import {
  useWatchedPages,
  useAddWatchedPages,
  useDeleteWatchedPages,
} from '../../../../shared/api/hooks/watched-pages/useWatchedPages';
import { WatchedPageResponse } from '../../../../shared/services/watched-pages/WatchedPagesService';
import AddPagesDialog from '../add-pages-dialog/AddPagesDialog';
import usePermission from '../../../../shared/hooks/global/UsePermission';
import useIsMobile from '../../../../shared/hooks/global/UseIsMobile';

interface WatchedPagesSectionProps {
  projectId: number;
  firmName: string;
  chatType: string;
  tags?: TagsResponse[];
  hasAdvancedFeatures?: boolean;
}

const WatchedPagesSection: React.FC<WatchedPagesSectionProps> = ({
  projectId,
  firmName,
  chatType,
  tags = [],
  hasAdvancedFeatures = false,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const projectsAccess = usePermission('PROJECTS');
  const dataTableRef = useRef<PrimeDataTable<WatchedPageResponse[]>>(null);

  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedPages, setSelectedPages] = useState<WatchedPageResponse[]>([]);
  const [addDialogVisible, setAddDialogVisible] = useState(false);

  const { data: watchedPages = [], isLoading } = useWatchedPages(projectId, chatType);
  const { mutate: addPages, isPending: isAdding } = useAddWatchedPages(projectId);
  const { mutate: deletePages } = useDeleteWatchedPages(projectId);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const filteredPages = useMemo(() => {
    if (!debouncedSearch.trim()) return watchedPages;
    const lower = debouncedSearch.toLowerCase();
    return watchedPages.filter(
      (p) => p.url.toLowerCase().includes(lower) || (p.domain && p.domain.toLowerCase().includes(lower))
    );
  }, [watchedPages, debouncedSearch]);

  const handleSave = useCallback(
    (urls: string[]) => {
      addPages(urls);
    },
    [addPages]
  );

  const handleBulkDelete = useCallback(() => {
    const ids = selectedPages.map((p) => p.id);
    deletePages(ids, {
      onSuccess: () => {
        setSelectedPages([]);
        setSelectionMode(false);
      },
    });
  }, [selectedPages, deletePages]);

  const formatShare = (share: number | string): string => {
    const val = Number(share);
    if (!val) return '-';
    return `${val.toFixed(2)}%`;
  };

  const getColumns = () => {
    const cols = [
      <Column
        key="index"
        header="#"
        headerClassName="table__header table__header--center"
        bodyClassName="table__cell--center table__cell--stack-rank"
        body={(_rowData: WatchedPageResponse, { rowIndex }: { rowIndex: number }) => rowIndex + 1}
        style={{ width: '40px', minWidth: '40px' }}
        exportable={false}
      />,
      <Column
        key="url"
        field="url"
        header={t('page')}
        headerClassName="table__header"
        bodyClassName="table__cell--stack-title"
        body={(rowData: WatchedPageResponse) => (
          <div className="source-data">
            {rowData.domain && (
              <span
                className="source-data__icon"
                style={{ backgroundImage: `url("https://favicon.is/${rowData.domain}")` }}
              />
            )}
            <CustomLink url={rowData.url} target="_blank" addHttps={true} />
          </div>
        )}
        style={{ minWidth: '160px', maxWidth: '300px' }}
      />,
      <Column
        key="category"
        field="category"
        header={t('category')}
        headerClassName="table__header table__header--center"
        bodyClassName="table__cell--center"
        body={(rowData: WatchedPageResponse) => {
          if (!rowData.category) return '-';
          const modifier = rowData.category.toLowerCase();
          return (
            <span
              className={`watched-pages-section__category watched-pages-section__category--${modifier}`}
            >
              {rowData.category}
            </span>
          );
        }}
      />,
      ...(hasAdvancedFeatures
        ? [
            <Column
              key="brandMentioned"
              field="isVerified"
              header={t('mentionedOnPage')}
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center"
              body={(rowData: WatchedPageResponse, options: { rowIndex: number }) => {
                const brandMentioned = isFirmMentionedInBrands(rowData.brands, firmName, tags);
                const { variant, statusText, tooltipText } = getVerifiedBrandStatus(
                  rowData.isVerified,
                  brandMentioned,
                  firmName,
                  t
                );
                return (
                  <MetadataBadge
                    variant={variant}
                    text={statusText}
                    tooltipText={tooltipText}
                    uniqueId={`watched-brand-${options.rowIndex}`}
                  />
                );
              }}
              style={{ width: '100px', minWidth: '100px' }}
            />,
          ]
        : []),
      <Column
        key="firstSeen"
        field="firstSeen"
        header={t('firstSeen')}
        headerClassName="table__header"
        body={(rowData: WatchedPageResponse) =>
          rowData.firstSeen ? format(new Date(rowData.firstSeen), 'yyyy-MM-dd') : '-'
        }
        style={{ width: '140px', minWidth: '140px' }}
      />,
      <Column
        key="created"
        field="created"
        header={t('added')}
        headerClassName="table__header"
        body={(rowData: WatchedPageResponse) =>
          rowData.created ? format(new Date(rowData.created), 'yyyy-MM-dd') : '-'
        }
        style={{ width: '140px', minWidth: '140px' }}
      />,
      <Column
        key="share"
        field="share"
        header={t('share')}
        headerClassName="table__header table__header--center"
        bodyClassName="table__cell--center"
        body={(rowData: WatchedPageResponse) => formatShare(rowData.share)}
        style={{ width: '80px', minWidth: '80px' }}
      />,
      <Column
        key="isMentioned"
        field="isMentioned"
        header=""
        headerClassName="table__header table__header--center"
        bodyClassName="table__cell--center"
        body={(rowData: WatchedPageResponse) =>
          rowData.isMentioned ? <span className="ico--tick ico--xs watched-pages-section__tick" /> : '-'
        }
        style={{ width: '100px', minWidth: '100px' }}
      />,
    ];

    if (selectionMode) {
      cols.unshift(
        <Column
          key="checkbox"
          exportable={false}
          bodyClassName="table__cell--stack-select"
          headerStyle={{ width: '25px' }}
          header={
            <Checkbox
              checked={
                filteredPages.length > 0 &&
                filteredPages.every((p) => selectedPages.some((sp) => sp.id === p.id))
              }
              onChange={(e) => {
                if (e.checked) {
                  setSelectedPages((prev) => {
                    const ids = new Set(prev.map((p) => p.id));
                    return [...prev, ...filteredPages.filter((p) => !ids.has(p.id))];
                  });
                } else {
                  const pageIds = new Set(filteredPages.map((p) => p.id));
                  setSelectedPages((prev) => prev.filter((p) => !pageIds.has(p.id)));
                }
              }}
            />
          }
          body={(rowData: WatchedPageResponse) => (
            <Checkbox
              checked={selectedPages.some((p) => p.id === rowData.id)}
              onChange={(e) => {
                if (e.checked) {
                  setSelectedPages((prev) => [...prev, rowData]);
                } else {
                  setSelectedPages((prev) => prev.filter((p) => p.id !== rowData.id));
                }
              }}
            />
          )}
        />
      );
    }

    return cols;
  };

  const hasPages = watchedPages.length > 0;

  return (
    <div className="stats__panel watched-pages-section">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('watchedPages')}
          <CustomTooltip tooltipText="watchedPagesTooltip" addMargin={false} />
          {/* Telefon: dodawanie stron jako mały niebieski przycisk obok tytułu (zamiast w rzędzie akcji). */}
          {isMobile && projectsAccess.write && (
            <Button
              className="btn btn--sm watched-pages-section__title-add"
              onClick={() => setAddDialogVisible(true)}
              aria-label={t('addPages')}
            >
              <span className="ico--add ico--xs" />
            </Button>
          )}
        </div>

        <div className="page-box__buttons watched-pages-section__toolbar">
          {hasPages && selectionMode && selectedPages.length > 0 && (
            <div className="page-box__buttons-panel">
              <Button
                className="action-btn action-btn--danger"
                onClick={handleBulkDelete}
                id="watched-pages-bulk-delete-btn"
              >
                <span className="ico--trash ico--xs" />
              </Button>
              <CustomTooltip tooltipText="bulkRemove" targetRef="watched-pages-bulk-delete-btn" />
            </div>
          )}

          {hasPages && (
            <div className="input-search-box">
              <InputText
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={t('searchPlaceholder')}
                className="input-search"
              />
            </div>
          )}

          {/* Dodanie stron zapisuje się na projekcie, a tryb zaznaczania służy wyłącznie usuwaniu
              hurtem. Eksport idzie z samej tabeli (`exportCSV`), nie z serwera, ale wynoszenie danych
              do pliku jest dla gościa zamknięte tak samo jak na Overview i w tabeli źródeł. */}
          {projectsAccess.write && !isMobile && (
            <Button className="btn btn--sm btn--primary" onClick={() => setAddDialogVisible(true)}>
              <span className="ico--add ico--xs" />
              {t('addPages')}
            </Button>
          )}

          {hasPages && projectsAccess.write && (
            <>
              <Button
                className="action-btn"
                onClick={() => dataTableRef.current?.exportCSV()}
                id="watched-pages-export-btn"
              >
                <span className="ico--download ico--xs" />
              </Button>
              <CustomTooltip tooltipText="exportCsv" targetRef="watched-pages-export-btn" />

              <Button
                className={`action-btn${selectionMode ? ' action-btn--active' : ''}`}
                onClick={() => {
                  setSelectionMode((prev) => {
                    if (prev) setSelectedPages([]);
                    return !prev;
                  });
                }}
                id="watched-pages-select-btn"
              >
                <span className="ico--select ico--xs" />
              </Button>
              <CustomTooltip tooltipText="selectModeProjects" targetRef="watched-pages-select-btn" />
            </>
          )}
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="skeleton--table skeleton--table-4">
          <span />
          <span />
          <span />
          <span />
        </Skeleton>
      ) : !hasPages ? (
        <div className="stats-box watched-pages-section__empty">
          <EmptyPanel textKey="noWatchedPagesYet" size="xs" />
        </div>
      ) : (
        <DataTable
          ref={dataTableRef}
          stripedRows
          scrollable
          removableSort
          value={filteredPages}
          tableStyle={{ tableLayout: 'auto' }}
          tableClassName={`table ${selectionMode ? 'table--select' : ''}`}
          className="table-wrapper table-wrapper--stack"
          responsiveLayout="stack"
          exportFilename="watched-pages"
          emptyMessage={() => <EmptyPanel />}
          dataKey="id"
        >
          {getColumns()}
        </DataTable>
      )}

      <AddPagesDialog
        visible={addDialogVisible}
        onHide={() => setAddDialogVisible(false)}
        onSave={handleSave}
        isSaving={isAdding}
      />
    </div>
  );
};

export default WatchedPagesSection;
